import styled from '@emotion/styled';

import { Bar1D, Bar1DBar, Model, Result } from '@gameonly/core';
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import keyify from '../project/parameters/keyify';
import PdfLegend, { PdfLegendRow } from './PdfLegend';
import {
  SeedsTooltipComponent,
  SeedsTooltipRow,
  SimpleTooltipRow,
} from './SeedsTooltipComponent';

type NameType = string | number;
type ValueType = string | number;

const Container = styled.div`
  width: 100%;
  height: 100%;
`;

const NoDataContainer = styled.div`
  width: 100%;
  padding: 16px;
  height: 100%;
  border: 1px dashed rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  display: flex;
  gap: 4px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  p {
    text-align: center;
  }
`;

export const getCategoryTranslationPrefix = (
  model: Model | undefined,
  result: Result,
) => {
  if (!model) return 'error_prefix';
  return `model:${model?.name.toLowerCase()}.result.${result.code?.toLowerCase()}`;
};

const Bar1DComponent = ({
  result,
  model,
  compact,
}: {
  result: Bar1D;
  model?: Model;
  compact?: boolean;
}) => {
  const { t } = useTranslation(['project', 'model']);
  const translationPrefix = getCategoryTranslationPrefix(model, result);
  if (!result?.data?.length || result?.data?.length === 0) {
    return (
      <NoDataContainer>
        <p>{t('project:results-page.result-empty')}</p>
      </NoDataContainer>
    );
  }

  const labelFontSize = result.size === 'half' ? 10 : 14;

  return (
    <Container>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          width={500}
          height={300}
          data={result.data}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <XAxis
            dataKey="name"
            tick={{ fontSize: labelFontSize }}
            interval={0}
          />
          {!compact && <YAxis />}
          <Tooltip
            content={
              <CustomTooltipComponent translationPrefix={translationPrefix} />
            }
          />
          {(result.bars || []).map((bar: Bar1DBar) => (
            <Bar
              key={bar.name}
              dataKey={bar.name}
              unit={result.unit}
              stackId="a"
              fill={bar.color as string}
            />
          ))}
          {compact ? (
            <XAxis
              dataKey="name"
              tick={{ fontSize: labelFontSize }}
              interval={0}
              axisLine={{ stroke: 'black', strokeWidth: 2 }}
            />
          ) : (
            <XAxis
              dataKey="name"
              tick={{ fontSize: labelFontSize }}
              interval={0}
            />
          )}
        </BarChart>
      </ResponsiveContainer>
    </Container>
  );
};

export const getCategoryName = (
  t: (key: string) => string,
  translationPrefix: string | undefined,
  categoryName: string,
) => {
  return categoryName.startsWith('#')
    ? categoryName.slice(1)
    : translationPrefix
    ? t(`${translationPrefix}.data.${keyify(categoryName)}`)
    : '';
};

type CustomTooltipComponentProps = TooltipProps<ValueType, NameType> & {
  translationPrefix?: string;
};

const CustomTooltipComponent = ({
  active,
  payload,
  translationPrefix,
}: CustomTooltipComponentProps): JSX.Element | null => {
  const { t } = useTranslation(['project', 'model']);
  if (active && payload && payload.length > 0) {
    const reversedData = [...payload].reverse();
    return (
      <SeedsTooltipComponent>
        {reversedData.map((d: any) => (
          <SeedsTooltipRow
            key={d.name}
            category={getCategoryName(t, translationPrefix, d.name)}
            color={d.color}
            value={d.value}
            unit={d.unit}
          />
        ))}
      </SeedsTooltipComponent>
    );
  }
  return null;
};

const LegendContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  > div {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
}
`;

export const Bar1DLegend = ({
  result,
  model,
  forPDF,
  compact,
  color,
}: {
  result: Bar1D;
  model?: Model;
  forPDF?: boolean;
  compact?: boolean;
  color?: string;
}) => {
  const { t } = useTranslation(['project', 'model']);
  const { bars, data } = result;
  const unit = result.unit;
  const translationPrefix = getCategoryTranslationPrefix(model, result);
  const reversedBars = bars ? [...bars].reverse() : [];

  const isOnlyOneBar = data?.length && data?.length > 1; // in this case we don't display the values of each part of bar, just the names

  if (forPDF) {
    if (isOnlyOneBar) {
      return (
        <PdfLegend compact={compact} color={color}>
          {reversedBars.map((row: Bar1DBar) => (
            <PdfLegendRow
              key={row.name}
              color={row.color}
              category={t(`${translationPrefix}.data.${keyify(row.name)}`)}
              unit={t(`${translationPrefix}.unit`)}
              compact={compact}
            />
          ))}
        </PdfLegend>
      );
    } else {
      let total: number = 0;
      const row = data![0];
      for (const key in row) {
        if (typeof row[key] === 'number' && key !== 'name') {
          total += row[key] as number;
        }
      }
      return (
        <PdfLegend compact={compact} color={color}>
          {reversedBars?.map((bar: Bar1DBar) => (
            <>
              {data![0][bar.name] && (
                <PdfLegendRow
                  key={bar.name}
                  color={bar.color}
                  category={getCategoryName(t, translationPrefix, bar.name)}
                  value={data![0][bar.name].toString() || ''}
                  unit={unit}
                  percent={Number(data![0][bar.name]) / total}
                  compact={compact}
                />
              )}
            </>
          ))}
        </PdfLegend>
      );
    }
  }

  if (isOnlyOneBar) {
    return (
      <LegendContainer>
        <p className="h6r underline">{t('project:results-page.legend')}</p>
        <div>
          {reversedBars?.map((row: Bar1DBar) => (
            <>
              {
                <SimpleTooltipRow
                  key={row.name}
                  color={row.color}
                  category={getCategoryName(t, translationPrefix, row.name)}
                  unit={unit}
                  //   link={row.link}
                />
              }
            </>
          ))}
        </div>
      </LegendContainer>
    );
  } else {
    let total: number = 0;
    const row = data![0];
    for (const key in row) {
      if (typeof row[key] === 'number' && key !== 'name') {
        total += row[key] as number;
      }
    }
    return (
      <LegendContainer>
        <p className="h6r underline">{t('project:results-page.legend')}</p>
        <div>
          {reversedBars?.map((bar: Bar1DBar) => (
            <>
              {data![0][bar.name] && (
                <SeedsTooltipRow
                  key={bar.name}
                  color={bar.color}
                  category={getCategoryName(t, translationPrefix, bar.name)}
                  value={data![0][bar.name].toString() || ''}
                  unit={unit}
                  percent={Number(data![0][bar.name]) / total}
                  // link={row.link}
                />
              )}
            </>
          ))}
        </div>
      </LegendContainer>
    );
  }
};

export default Bar1DComponent;
